import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useChat, ChatMessage, Conversation, TypingIndicator } from './useChatSystem';
import { toast } from '@/hooks/use-toast';

// Re-export types for easier importing
export type { ChatMessage, Conversation, TypingIndicator } from './useChatSystem';

export interface MemberInfo {
  id: string;
  name: string;
  firstname: string;
  lastname: string;
  avatar_url?: string;
  company?: string;
  role?: string;
  bio?: string;
  is_online?: boolean;
}

export interface ChatSearchResult {
  type: 'conversation' | 'message';
  id: string;
  conversation_id?: string;
  content: string;
  member_info?: MemberInfo;
  created_at: string;
}

export interface VoiceMessage {
  id: string;
  audio_url: string;
  duration_seconds: number;
  transcription?: string;
}

export interface SharedResource {
  type: 'insight' | 'opportunity' | 'event' | 'member' | 'workshop';
  id: string;
  title: string;
  description?: string;
  url?: string;
  image_url?: string;
  metadata?: any;
}

// Bot system constant
const BOT_ID = '00000000-0000-0000-0000-000000000001';
const BOT_INFO: MemberInfo = {
  id: BOT_ID,
  name: 'FirstTuesday Bot',
  firstname: 'FirstTuesday',
  lastname: 'Bot',
  avatar_url: undefined,
  company: 'FirstTuesday',
  role: 'Assistant IA',
  bio: 'Assistant automatique de FirstTuesday'
};

export const useOptimizedChat = () => {
  const { user } = useAuth();
  const baseChat = useChat();
  const [membersCache, setMembersCache] = useState<Map<string, MemberInfo>>(new Map());
  const [searchResults, setSearchResults] = useState<ChatSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [notificationsEnabled, setNotificationsEnabled] = useState(false);
  const audioRef = useRef<HTMLAudioElement>();

  // Load members info and cache them
  const loadMemberInfo = useCallback(async (userIds: string[]) => {
    const newCache = new Map(membersCache);
    
    // Handle bot specially - add to cache without DB query
    if (userIds.includes(BOT_ID) && !membersCache.has(BOT_ID)) {
      newCache.set(BOT_ID, BOT_INFO);
    }
    
    // Filter out bot ID and already cached IDs
    const uncachedIds = userIds.filter(id => !membersCache.has(id) && id !== BOT_ID);
    if (uncachedIds.length === 0) {
      if (newCache.size > membersCache.size) {
        setMembersCache(newCache);
      }
      return;
    }

    try {
      const { data: members, error } = await supabase
        .from('club_members')
        .select('user_id, name, firstname, lastname, image, company, role, bio')
        .in('user_id', uncachedIds);

      if (error) throw error;

      members?.forEach(member => {
        if (member.user_id) {
          newCache.set(member.user_id, {
            id: member.user_id,
            name: member.name || `${member.firstname || ''} ${member.lastname || ''}`.trim(),
            firstname: member.firstname || '',
            lastname: member.lastname || '',
            avatar_url: member.image,
            company: member.company,
            role: member.role,
            bio: member.bio
          });
        }
      });
      
      setMembersCache(newCache);
    } catch (error) {
      console.error('Error loading member info:', error);
    }
  }, [membersCache]);

  // Enhanced conversations with member info
  const enrichedConversations = baseChat.conversations.map(conv => {
    const otherParticipantId = conv.participant_1_id === user?.id 
      ? conv.participant_2_id 
      : conv.participant_1_id;
    
    return {
      ...conv,
      memberInfo: membersCache.get(otherParticipantId)
    };
  });

  // Search in conversations and messages
  const searchChats = useCallback(async (query: string) => {
    if (!query.trim() || !user) {
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      // Search in messages
      const { data: messageResults, error: messageError } = await supabase
        .from('chat_messages')
        .select(`
          id,
          conversation_id,
          content,
          sender_id,
          created_at
        `)
        .or(`sender_id.eq.${user.id},recipient_id.eq.${user.id}`)
        .ilike('content', `%${query}%`)
        .order('created_at', { ascending: false })
        .limit(20);

      if (messageError) throw messageError;

      // Load member info for search results
      const senderIds = messageResults?.map(msg => msg.sender_id) || [];
      await loadMemberInfo(senderIds);

      // Format search results
      const formattedResults: ChatSearchResult[] = messageResults?.map(msg => ({
        type: 'message' as const,
        id: msg.id,
        conversation_id: msg.conversation_id,
        content: msg.content,
        member_info: membersCache.get(msg.sender_id),
        created_at: msg.created_at
      })) || [];

      setSearchResults(formattedResults);
    } catch (error) {
      console.error('Error searching chats:', error);
      toast({
        title: "Erreur de recherche",
        description: "Impossible de rechercher dans les conversations",
        variant: "destructive"
      });
    } finally {
      setIsSearching(false);
    }
  }, [user, loadMemberInfo, membersCache]);

  // Send voice message
  const sendVoiceMessage = useCallback(async (
    conversationId: string,
    recipientId: string,
    audioBlob: Blob,
    duration: number
  ) => {
    if (!user) return null;

    try {
      // Upload audio to storage
      const fileName = `voice_${Date.now()}.webm`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(`voice-messages/${fileName}`, audioBlob);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(uploadData.path);

      // Transcribe audio using edge function
      const { data: transcriptionData } = await supabase.functions.invoke('transcribe-audio', {
        body: { audioUrl: publicUrl }
      });

      // Send voice message
      const voiceData: VoiceMessage = {
        id: uploadData.path,
        audio_url: publicUrl,
        duration_seconds: duration,
        transcription: transcriptionData?.transcription
      };

      const message = await baseChat.sendMessage(
        conversationId,
        transcriptionData?.transcription || '[Message vocal]',
        recipientId,
        'voice'
      );

      return { ...message, voice_data: voiceData };
    } catch (error) {
      console.error('Error sending voice message:', error);
      toast({
        title: "Erreur",
        description: "Impossible d'envoyer le message vocal",
        variant: "destructive"
      });
      return null;
    }
  }, [user, baseChat.sendMessage]);

  // Share resource in chat
  const shareResource = useCallback(async (
    conversationId: string,
    recipientId: string,
    resource: SharedResource
  ) => {
    if (!user) return null;

    try {
      const shareMessage = `📎 Partage: ${resource.title}\n${resource.description || ''}`;
      
      const message = await baseChat.sendMessage(
        conversationId,
        shareMessage,
        recipientId,
        'text'
      );

      // Store shared resource metadata
      await supabase
        .from('chat_messages')
        .update({
          metadata: JSON.stringify({
            shared_resource: resource
          })
        })
        .eq('id', message?.id);

      return message;
    } catch (error) {
      console.error('Error sharing resource:', error);
      toast({
        title: "Erreur",
        description: "Impossible de partager la ressource",
        variant: "destructive"
      });
      return null;
    }
  }, [user, baseChat.sendMessage]);

  // Get or create conversation (optimized query)
  const getOrCreateConversation = useCallback(async (otherUserId: string) => {
    if (!user) return null;

    try {
      // Try to find existing conversation with simplified query
      const { data: existingConversation } = await supabase
        .from('conversations')
        .select('*')
        .or(`participant_1_id.eq.${user.id},participant_2_id.eq.${user.id}`)
        .or(`participant_1_id.eq.${otherUserId},participant_2_id.eq.${otherUserId}`)
        .limit(1)
        .single();

      if (existingConversation) {
        return existingConversation;
      }

      // Create new conversation
      const { data: newConversation, error } = await supabase
        .from('conversations')
        .insert({
          participant_1_id: user.id,
          participant_2_id: otherUserId,
          last_message_at: new Date().toISOString()
        })
        .select()
        .single();

      if (error) throw error;
      return newConversation;
    } catch (error) {
      console.error('Error getting/creating conversation:', error);
      return null;
    }
  }, [user]);

  // Enhanced send message with analytics
  const sendMessageWithAnalytics = useCallback(async (
    conversationId: string,
    content: string,
    recipientId: string,
    messageType: 'text' | 'voice' | 'image' | 'file' | 'ai_response' = 'text'
  ) => {
    const startTime = Date.now();
    
    try {
      const message = await baseChat.sendMessage(conversationId, content, recipientId, messageType);
      
      // Track message analytics
      const responseTime = Date.now() - startTime;
      
      // Store analytics
      await supabase
        .from('networking_events')
        .insert({
          user_id: user?.id,
          target_user_id: recipientId,
          event_type: 'message_sent',
          metadata: {
            conversation_id: conversationId,
            message_type: messageType,
            response_time_ms: responseTime,
            content_length: content.length,
            timestamp: new Date().toISOString()
          }
        });

      return message;
    } catch (error) {
      console.error('Error sending message with analytics:', error);
      throw error;
    }
  }, [user, baseChat.sendMessage]);

  // Message moderation
  const moderateMessage = useCallback(async (content: string) => {
    try {
      const { data, error } = await supabase.functions.invoke('moderate-content', {
        body: { content }
      });

      if (error) throw error;
      
      return {
        isAppropriate: data?.isAppropriate ?? true,
        flaggedReasons: data?.flaggedReasons || [],
        moderatedContent: data?.moderatedContent || content
      };
    } catch (error) {
      console.error('Error moderating message:', error);
      return {
        isAppropriate: true,
        flaggedReasons: [],
        moderatedContent: content
      };
    }
  }, []);

  // Request notification permission
  const requestNotificationPermission = useCallback(async () => {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      console.warn('Notifications not supported on this browser');
      return false;
    }
    
    try {
      const permission = await Notification.requestPermission();
      setNotificationsEnabled(permission === 'granted');
      return permission === 'granted';
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }, []);

  // Show notification for new message
  const showNotification = useCallback((message: any, senderName: string) => {
    if (!notificationsEnabled || document.hasFocus()) return;
    if (typeof window === 'undefined' || typeof window.Notification === 'undefined') return;
    if (!('Notification' in window)) return;

    try {
      if (window.Notification.permission !== 'granted') return;
      
      const notification = new window.Notification(`Nouveau message de ${senderName}`, {
        body: message.content.substring(0, 100),
        icon: '/favicon.ico',
        tag: message.conversation_id
      });

      notification.onclick = () => {
        window.focus();
        notification.close();
      };
    } catch (error) {
      console.warn('Browser notification not supported:', error);
    }

    // Play notification sound
    if (audioRef.current) {
      audioRef.current.play().catch(() => {
        // Ignore autoplay errors
      });
    }
  }, [notificationsEnabled]);

  // Load member info when conversations change
  useEffect(() => {
    const participantIds = enrichedConversations.flatMap(conv => [
      conv.participant_1_id,
      conv.participant_2_id
    ]).filter(id => id !== user?.id);
    
    if (participantIds.length > 0) {
      loadMemberInfo(participantIds);
    }
  }, [baseChat.conversations, user?.id, loadMemberInfo]);

  // Setup notification permission on mount
  useEffect(() => {
    if (typeof window !== 'undefined' && 'Notification' in window && typeof window.Notification !== 'undefined') {
      if (window.Notification.permission === 'default') {
        requestNotificationPermission();
      } else {
        setNotificationsEnabled(window.Notification.permission === 'granted');
      }
    }
  }, [requestNotificationPermission]);

  return {
    ...baseChat,
    enrichedConversations,
    membersCache,
    searchResults,
    isSearching,
    notificationsEnabled,
    searchChats,
    sendVoiceMessage,
    shareResource,
    sendMessageWithAnalytics,
    moderateMessage,
    requestNotificationPermission,
    showNotification,
    loadMemberInfo,
    getOrCreateConversation,
    fetchMessages: baseChat.fetchMessages // Expose fetchMessages for manual loading
  };
};